import {
  PartnerPaymentStatus,
  useGetPartnersPaymentsByIdQuery,
} from '@/models/payments';
import { Button, CircularProgress, Divider, Typography } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs from 'dayjs';
import { Card } from '@/features/card';
import { VAT_FALLBACK } from '@/features/summary';
import { PaymentStatus } from './PaymentStatus';
import { useTranslation } from 'react-i18next';
import ReplayIcon from '@mui/icons-material/Replay';
import { SummaryLine } from './SummaryLine';
import { useMemo } from 'react';
import InvoiceUpload from './InvoiceUpload';

export const PaymentDetails = () => {
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useGetPartnersPaymentsByIdQuery(
    {
      id: id ?? '',
    },
    { skip: !id }
  );
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const formattedAsCurrency = (amount: string) =>
    `${new Intl.NumberFormat(i18n.language).format(Number(amount))} zł`;

  const { bonus, netTotal, grossTotal } = useMemo(() => {
    if (!data) return { bonus: 0, netTotal: 0, grossTotal: 0 };
    const bonus = Number(data.bonus) || 0;
    const netTotal = Number(data.amount) + bonus;
    const grossTotal = netTotal + VAT_FALLBACK * netTotal;
    return { bonus, netTotal, grossTotal };
  }, [data]);

  if (!id) {
    return <Navigate to="/partner/payments" />;
  }

  return (
    <div className="w-full mt-5 mb-10 flex-1 px-4">
      {isLoading && (
        <div className="flex justify-center items-center">
          <CircularProgress />
        </div>
      )}
      {error && (
        <div className="flex flex-col gap-3 justify-center items-center text-center text-gray-500 min-h-40">
          <p>
            {('data' in error &&
              (error.data as { message?: string })?.message) ||
              t('taxi_partner.payment_details.fetch_error')}
          </p>

          <Button
            startIcon={<ReplayIcon />}
            variant="contained"
            onClick={refetch}
          >
            {t('taxi_partner.payment_details.reload')}
          </Button>
        </div>
      )}

      {data && (
        <div>
          <div>
            <Button
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() => navigate(-1)}
            >
              {t('taxi_partner.payment_details.back_to_payments')}
            </Button>

            <Typography variant="h4" gutterBottom>
              {dayjs(data.paymentMonth).format('MMMM YYYY')}
            </Typography>
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-[2fr_1fr] gap-6 mt-6 lg:items-start">
            <div className="flex flex-col gap-8">
              <Card className="flex flex-col gap-8">
                <div className="flex justify-between">
                  <span>
                    {t('taxi_partner.payment_details.payment_amount')}
                  </span>
                  <div className="flex flex-col items-end">
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: '600', fontSize: 30 }}
                    >
                      {formattedAsCurrency(grossTotal.toFixed(2))}
                    </Typography>
                    <span className="text-xs text-gray-500">
                      {t('taxi_partner.payment_details.total_gross')}
                    </span>
                  </div>
                </div>

                <div
                  className="bg-gray-100/50 px-3 py-3 rounded-lg flex flex-col gap-2"
                  data-testid="summary-line-container"
                >
                  <SummaryLine
                    property={t('taxi_partner.payment_details.payment_for')}
                    value={dayjs(data.paymentMonth).format('MMMM YYYY')}
                  />
                  <SummaryLine
                    property={t('taxi_partner.payment_details.earned_pln_net')}
                    value={formattedAsCurrency(data.amount)}
                  />
                  <SummaryLine
                    property={t('taxi_partner.payment_details.bonus_pln_net')}
                    value={formattedAsCurrency(bonus.toFixed(2))}
                  />
                  <Divider />
                  <SummaryLine
                    property={t('taxi_partner.payment_details.total_pln_net')}
                    value={formattedAsCurrency(netTotal.toFixed(2))}
                    isBold
                  />
                  <SummaryLine
                    property={t('taxi_partner.payment_details.total_pln_gross')}
                    value={formattedAsCurrency(grossTotal.toFixed(2))}
                    isBold
                  />
                </div>
              </Card>

              {data.status === PartnerPaymentStatus.WAITING_FOR_INVOICE && (
                <Card className="lg:mb-10">
                  <InvoiceUpload id={id} />
                </Card>
              )}
            </div>

            <PaymentStatus status={data.status} />
          </div>
        </div>
      )}
    </div>
  );
};
